/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useEffect, useCallback } from "react";

import {
  Typography,
  Grid
} from "@mui/material";
import { useReactToPrint } from 'react-to-print';
import msgNotificacion from '../../scripts/notification';
import LogoFS from '../../assets/images/logoFS.png';
import Formatter from "../../scripts/formatters";
import '../../styles/solicitudes.css';

const FormHorasExtrasPdf = ({ data, print, setPrint }) => {
  const componentRef = useRef(null);

  // Función para formatear horas a AM/PM
  const formatTime = (timeValue) => {
    if (!timeValue) return '';
    
    try {
      // Si viene como string de hora (HH:MM:SS), convertirlo
      if (typeof timeValue === 'string' && timeValue.includes(':') && !timeValue.includes('T')) {
        const [hours, minutes] = timeValue.split(':');
        const hour = parseInt(hours, 10);
        const min = parseInt(minutes, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${min.toString().padStart(2, '0')} ${ampm}`;
      }
      
      // Si viene como string ISO (1970-01-01T17:00:00.000Z), extraer solo la hora
      if (typeof timeValue === 'string' && timeValue.includes('T')) {
        const timePart = timeValue.split('T')[1];
        if (timePart) {
          const timeWithoutMs = timePart.split('.')[0]; // Remover milisegundos
          const [hours, minutes] = timeWithoutMs.split(':');
          const hour = parseInt(hours, 10);
          const min = parseInt(minutes, 10);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const displayHour = hour % 12 || 12;
          return `${displayHour}:${min.toString().padStart(2, '0')} ${ampm}`;
        }
      }
      
      // Si viene como Date, extraer solo la hora
      const date = new Date(timeValue);
      if (!isNaN(date.getTime())) {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHour = hours % 12 || 12;
        return `${displayHour}:${minutes.toString().padStart(2, '0')} ${ampm}`;
      }
      
      return timeValue; // Fallback
    } catch (error) {
      return timeValue; // Fallback
    }
  };

  // useCallback para memorizar funciones y evitar re-renderizaciones innecesarias.
  const handleAfterPrint = useCallback(() => {
    msgNotificacion('Impresión exitosa', 'success', 500);
    setPrint(false);
  }, [setPrint]);

  const handleBeforeContent = useCallback(() => {
    msgNotificacion('Imprimiendo...', 'info', 1000);
  }, []);

  const handlePrint = useReactToPrint({
    documentTitle: 'FORMULARIO DE HORAS EXTRAS',
    content: () => componentRef.current,
    onAfterPrint: handleAfterPrint,
    onBeforeGetContent: handleBeforeContent,
  });

  // Utilice una función separada para la lógica de impresión real para mejorar la legibilidad
  const triggerPrint = useCallback(() => {
    if (componentRef.current) {
      setTimeout(() => {
        handlePrint()
      }, 500)
    }
  }, [handlePrint]);

  useEffect(() => {
    if (print) {
      triggerPrint()
    }
  }, [print, triggerPrint]);

  return (    
    <div style={{ padding: 1, margin: 1 }} className='no-print'>
      <div ref={componentRef} className='print-container'>
        {
          data?.map((row) => {
            return (
              <div key={row.IdHorasExtras} className='vh-100 d-flex flex-column align-items-center'>
                <div className='d-flex align-items-center justify-content-center my-1 py-4'>
                  <img src={LogoFS} alt='Logo contrata' height={110} width={240} className='me-4' />

                  <h4 className='text-center fw-bolder'>
                    <span>FORMULARIO DE HORAS EXTRAS</span>
                    <p className='no-solicitud'>No. Solicitud: {row.IdHorasExtras}</p>
                  </h4>
                </div>

                <Grid container spacing={2} className='px-5 border py-4'>
                  {/* Fila #1 encabezado */}
                  <Grid item xs={2} md={2} className='pt-2'>
                    <Typography fontWeight='bold'>CÓDIGO:</Typography>
                  </Grid>
                  <Grid item xs={2} md={5} className='pt-2'>
                    <Typography className='text-decoration-underline'>{row?.CodEmpleado}</Typography>
                  </Grid>
                  <Grid item xs={2} md={2} className='pt-2'>
                    <Typography fontWeight='bold'>NOMBRE:</Typography>
                  </Grid>
                  <Grid item xs={6} md={3} className='pt-2'>
                    <Typography className='text-decoration-underline'>{row?.NombreEmpleado?.toUpperCase()}</Typography>
                  </Grid>
                  
                  {/* Fila #2 encabezado */}
                  <Grid item xs={3} md={2} className='pt-2'>
                    <Typography fontWeight='bold'>FECHA DE TRABAJO:</Typography>
                  </Grid>
                  <Grid item xs={3} md={5} className='pt-2'>
                    <Typography className='text-decoration-underline'>{Formatter.dateFormatter(row?.FechaTrabajo)}</Typography>
                  </Grid>
                  <Grid item xs={3} md={2} className='pt-2'>
                    <Typography fontWeight='bold'>TIPO:</Typography>
                  </Grid>
                  <Grid item xs={3} md={3} className='pt-2'>
                    <Typography className='text-decoration-underline'>{row?.TipoMarcacion}</Typography>
                  </Grid>

                  {/* Fila #3 encabezado */}
                  <Grid item xs={3} md={2} className='pt-2'>
                    <Typography fontWeight='bold'>HORA ESPERADA:</Typography>
                  </Grid>
                  <Grid item xs={3} md={5} className='pt-2'>
                    <Typography className='text-decoration-underline'>{formatTime(row?.HoraEsperada)}</Typography>
                  </Grid>
                  <Grid item xs={3} md={2} className='pt-2'>
                    <Typography fontWeight='bold'>HORA REAL:</Typography>
                  </Grid>
                  <Grid item xs={3} md={3} className='pt-2'>
                    <Typography className='text-decoration-underline'>{formatTime(row?.HoraReal)}</Typography>
                  </Grid>

                  {/* Fila #4 encabezado */}
                  <Grid item xs={3} md={2} className='pt-2'>
                    <Typography fontWeight='bold'>HORAS EXTRAS:</Typography>
                  </Grid>
                  <Grid item xs={3} md={5} className='pt-2'>
                    <Typography className='text-decoration-underline'>{row?.CantidadHoras} horas</Typography>
                  </Grid>
                  <Grid item xs={3} md={2} className='pt-2'>
                    <Typography fontWeight='bold'>MOTIVO:</Typography>
                  </Grid>
                  <Grid item xs={3} md={3} className='pt-2'>
                    <Typography className='text-decoration-underline'>{row?.Motivo || 'Sin motivo especificado'}</Typography>
                  </Grid>


                  {/* Firma */}
                  <Grid container spacing={2} className='px-5 py-5 mt-1 border-bottom justify-content-between'>
                    <Grid item xs={3} md={3} textAlign='center'>
                      <Typography className='text-decoration-underline' fontSize={10}>{row?.NombreEmpleado}</Typography>
                      <Typography fontWeight='bolder'>Solicita</Typography>
                    </Grid>
                    <Grid item xs={3} md={3} textAlign='center'>
                      <Typography className='text-decoration-underline' fontSize={10}>{row?.JefeInmediato}</Typography>
                      <Typography fontWeight='bolder'>Autoriza</Typography>
                    </Grid>
                    <Grid item xs={3} md={3} textAlign='center'>
                      <Typography className='text-decoration-underline' fontSize={10}>{row?.JefeRRHH}</Typography>
                      <Typography fontWeight='bolder'>Recibe RRHH</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
            )
          })
        }
      </div>
    </div>
  );
};

export default FormHorasExtrasPdf;
